import { useEffect, useState, useCallback } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { track } from '../lib/paywall';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ReceiptsTable } from '../components/ReceiptsTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CreditCard, Receipt, Briefcase, ExternalLink, Plus, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const fmtPence = (p) => p == null ? '—' : `£${(p / 100).toLocaleString('en-GB', { minimumFractionDigits: 2 })}`;
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const STATUS_BADGE = {
  active: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/30',
  trialing: 'bg-info-blue/10 text-info-blue border-info-blue/30',
  past_due: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
  canceled: 'bg-red-500/10 text-red-700 border-red-500/30',
};

export default function AccountPage() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(true);
  const [commissions, setCommissions] = useState([]);
  const [polling, setPolling] = useState(false);
  const [pollResult, setPollResult] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [s, i, c] = await Promise.all([
        api.get('/billing/subscription'),
        api.get('/billing/receipts').catch(() => ({ data: { receipts: [] } })),
        api.get('/billing/commissions').catch(() => ({ data: { commissions: [] } })),
      ]);
      setSub(s.data);
      setReceipts(i.data.receipts || []);
      setReceiptsLoading(false);
      setCommissions(c.data.commissions || []);
    } catch (e) {
      toast.error('Could not load account data');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Return from the one-click card update link
  useEffect(() => {
    const card = params.get('card');
    if (!card) return;
    if (card === 'updated') toast.success('Card updated — Stripe will retry your renewal automatically', { id: 'card-updated', duration: 8000 });
    else if (card === 'error') toast.error('We could not open the card update page. Use "Manage billing" below.', { id: 'card-error' });
    const next = new URLSearchParams(params); next.delete('card'); setParams(next, { replace: true });
  }, [params, setParams]);

  // Poll on Stripe return
  useEffect(() => {
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    let attempts = 0;
    let cancelled = false;
    setPolling(true);
    const tick = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/billing/checkout/${sessionId}`);
        if (data.payment_status === 'paid' || data.fulfilled) {
          setPolling(false);
          setPollResult({ ok: true, type: data.type });
          toast.success(data.type === 'subscription' ? 'Subscription activated!' : 'Payment received — thank you.');
          track(data.type === 'subscription' ? 'subscribed' : 'checkout_completed', {
            source: 'account_polling',
            plan_id: data.plan_id || data.credit_type || data.type,
          });
          await checkAuth();
          await refresh();
          // Clear session_id from URL
          const next = new URLSearchParams(params);
          next.delete('session_id');
          setParams(next, { replace: true });
          return;
        }
        if (data.status === 'expired' || attempts >= 8) {
          setPolling(false);
          setPollResult({ ok: false });
          return;
        }
      } catch {
        if (attempts >= 8) { setPolling(false); setPollResult({ ok: false }); return; }
      }
      attempts += 1;
      setTimeout(tick, 2000);
    };
    tick();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openPortal = async () => {
    try {
      const { data } = await api.post('/billing/portal', { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const buyCredits = async (lookup) => {
    try {
      const { data } = await api.post('/billing/checkout/credits', {
        credit_lookup: lookup,
        quantity: 1,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (!sub) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-ink/40" /></div>;

  const LA_LABEL = { stockton: 'Stockton-on-Tees', middlesbrough: 'Middlesbrough', redcar: 'Redcar & Cleveland', hartlepool: 'Hartlepool', darlington: 'Darlington' };
  const planLabel = sub.district_licence
    ? `${LA_LABEL[sub.district_licence] || sub.district_licence} district licence`
    : ({ free: 'Free', pro: 'Pro', premium: 'Premium', enterprise: 'Enterprise' }[sub.plan_id] || sub.plan_id);
  const isPaid = sub.plan_id !== 'free' || Boolean(sub.district_licence);
  const promptCreditLookup = sub.plan_id === 'premium' ? 'prompt_prem' : 'prompt_pro';
  const reportCreditLookup = sub.plan_id === 'premium' ? 'report_prem' : 'report_pro';
  const promptUnit = sub.plan_id === 'premium' ? 3 : 5;
  const reportUnit = sub.plan_id === 'premium' ? 5 : 8;

  const promptUsage = sub.is_daily_metered
    ? `${sub.ai_prompts_used_today} / 2 today`
    : sub.ai_prompts_limit === -1
      ? 'Unlimited'
      : `${sub.ai_prompts_used} / ${sub.ai_prompts_limit} this period`;
  const reportUsage = sub.reports_limit === -1
    ? 'Unlimited'
    : sub.reports_limit === 0
      ? 'Not included on Free'
      : `${sub.reports_used} / ${sub.reports_limit} this period`;

  return (
    <div className="min-h-screen bg-page-bg" data-testid="account-page">
      <div className="max-w-[1100px] mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <p className="font-body text-xs text-ink/50 uppercase tracking-wide mb-1">Account</p>
            <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-ink">{user?.name || user?.email}</h1>
            <p className="font-body text-sm text-ink/50 mt-1">{user?.email}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/pricing')} data-testid="open-pricing-btn" className="font-body text-sm">
              Change plan
            </Button>
            {isPaid && (
              <Button onClick={openPortal} className="bg-ink hover:bg-ink/90 text-white font-body text-sm" data-testid="open-portal-btn">
                Manage billing <ExternalLink className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            )}
          </div>
        </div>

        {/* Polling banner */}
        {polling && (
          <div className="mb-6 rounded-[6px] border border-info-blue/30 bg-info-blue/5 p-4 flex items-center gap-3" data-testid="polling-banner">
            <Loader2 className="w-5 h-5 animate-spin text-info-blue" />
            <div>
              <p className="font-body text-sm font-semibold text-ink">Confirming your payment with Stripe…</p>
              <p className="font-body text-xs text-ink/60">This usually takes a few seconds.</p>
            </div>
          </div>
        )}
        {pollResult?.ok && (
          <div className="mb-6 rounded-[6px] border border-emerald-500/30 bg-emerald-500/5 p-4 flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <p className="font-body text-sm font-semibold text-ink">Success! Your account has been updated.</p>
          </div>
        )}
        {pollResult && !pollResult.ok && (
          <div className="mb-6 rounded-[6px] border border-amber-500/30 bg-amber-500/5 p-4 flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <p className="font-body text-sm text-ink">Payment status is still pending. Please check back in a moment.</p>
          </div>
        )}

        {/* Plan summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-8">
          <div className="lg:col-span-2 rounded-[6px] border border-ink/10 bg-white p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="font-body text-xs text-ink/50 uppercase tracking-wide">Current plan</p>
                <h2 className="font-display text-2xl font-extrabold text-ink mt-1" data-testid="current-plan-label">{planLabel}</h2>
                {sub.plan_billing_period && <p className="font-body text-xs text-ink/50 capitalize mt-1">{sub.plan_billing_period} billing</p>}
                {sub.district_licence && <p className="font-body text-xs text-forest mt-1" data-testid="district-licence-note">Full access + unlimited queries in {LA_LABEL[sub.district_licence]}. Other districts: free tier.</p>}
              </div>
              {sub.subscription_status && (
                <Badge className={`${STATUS_BADGE[sub.subscription_status] || 'bg-ink/5 text-ink/60 border-ink/10'} font-body text-[11px] capitalize`} data-testid="subscription-status-badge">
                  {sub.subscription_status === 'trialing' ? 'Free trial' : sub.subscription_status.replace('_', ' ')}
                </Badge>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-ink/10">
              <div>
                <p className="font-body text-[11px] text-ink/50 uppercase tracking-wide">AI prompts</p>
                <p className="font-display text-lg font-extrabold text-ink mt-1" data-testid="ai-usage">{promptUsage}</p>
                {sub.ai_prompt_credits > 0 && <p className="font-body text-[11px] text-emerald-700 mt-0.5">+{sub.ai_prompt_credits} PAYG credits</p>}
              </div>
              <div>
                <p className="font-body text-[11px] text-ink/50 uppercase tracking-wide">Reports</p>
                <p className="font-display text-lg font-extrabold text-ink mt-1" data-testid="report-usage">{reportUsage}</p>
                {sub.report_credits > 0 && <p className="font-body text-[11px] text-emerald-700 mt-0.5">+{sub.report_credits} PAYG credits</p>}
              </div>
              {sub.trial_ends_at && (
                <div>
                  <p className="font-body text-[11px] text-ink/50 uppercase tracking-wide">Trial ends</p>
                  <p className="font-body text-sm text-ink mt-1">{fmtDate(sub.trial_ends_at)}</p>
                </div>
              )}
              {sub.current_period_end && (
                <div>
                  <p className="font-body text-[11px] text-ink/50 uppercase tracking-wide">Next renewal</p>
                  <p className="font-body text-sm text-ink mt-1">{fmtDate(sub.current_period_end)}</p>
                </div>
              )}
            </div>
          </div>

          {/* PAYG */}
          <div className="rounded-[6px] border border-ink/10 bg-white p-6">
            <p className="font-body text-xs text-ink/50 uppercase tracking-wide mb-3">Pay-as-you-go credits</p>
            {!isPaid ? (
              <>
                <p className="font-body text-sm text-ink/60 mb-4">Subscribe to Pro or Premium to unlock PAYG top-ups.</p>
                <Button onClick={() => navigate('/pricing')} className="w-full bg-brand-orange hover:bg-brand-orange/90 text-white font-body text-sm">
                  See plans
                </Button>
              </>
            ) : (
              <div className="space-y-2">
                <Button
                  onClick={() => buyCredits(promptCreditLookup)}
                  data-testid="buy-prompt-credit-btn"
                  variant="outline"
                  className="w-full justify-between font-body text-sm hover:bg-brand-orange/5 hover:border-brand-orange/30"
                >
                  <span><Plus className="w-3.5 h-3.5 inline mr-1.5" />1 AI prompt credit</span>
                  <span className="font-display font-extrabold">£{promptUnit}</span>
                </Button>
                <Button
                  onClick={() => buyCredits(reportCreditLookup)}
                  data-testid="buy-report-credit-btn"
                  variant="outline"
                  className="w-full justify-between font-body text-sm hover:bg-brand-orange/5 hover:border-brand-orange/30"
                >
                  <span><Plus className="w-3.5 h-3.5 inline mr-1.5" />1 report credit</span>
                  <span className="font-display font-extrabold">£{reportUnit}</span>
                </Button>
                <p className="font-body text-[11px] text-ink/40 mt-2">Credits stack on top of your monthly allowance.</p>
              </div>
            )}
          </div>
        </div>

        {/* Tabs: invoices / commissions */}
        <Tabs defaultValue="invoices">
          <TabsList className="bg-ink/5">
            <TabsTrigger value="invoices" data-testid="tab-invoices" className="font-body text-sm"><Receipt className="w-3.5 h-3.5 mr-1.5" />Receipts</TabsTrigger>
            <TabsTrigger value="commissions" data-testid="tab-commissions" className="font-body text-sm"><Briefcase className="w-3.5 h-3.5 mr-1.5" />Commissions</TabsTrigger>
          </TabsList>

          <TabsContent value="invoices" className="mt-4">
            <ReceiptsTable receipts={receipts} loading={receiptsLoading} />
          </TabsContent>

          <TabsContent value="commissions" className="mt-4">
            <div className="rounded-[6px] border border-ink/10 bg-white overflow-hidden">
              {commissions.length === 0 ? (
                <div className="p-6">
                  <p className="font-body text-sm text-ink/50 mb-3">No commissions yet. Commission an audit, listing, or full uplift for any Stockton business.</p>
                  <Button asChild className="bg-ink hover:bg-ink/90 text-white font-body text-sm">
                    <Link to="/interventions/matrix">Open Priority Matrix</Link>
                  </Button>
                </div>
              ) : (
                <table className="w-full">
                  <thead className="bg-ink/[0.03] border-b border-ink/10">
                    <tr>
                      <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Business</th>
                      <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Tier</th>
                      <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Amount</th>
                      <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Date</th>
                      <th className="font-display text-xs font-extrabold text-ink p-3 text-left">Status</th>
                      <th className="font-display text-xs font-extrabold text-ink p-3 text-right">Certificate</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((c, i) => (
                      <tr key={i} className="border-b border-ink/5 last:border-0" data-testid={`commission-row-${i}`}>
                        <td className="p-3 font-body text-xs text-ink/80">{c.business_name}</td>
                        <td className="p-3 font-body text-xs text-ink/70 capitalize">{c.commission_type}</td>
                        <td className="p-3 font-body text-xs text-ink/80">{fmtPence(c.amount_paid_gbp)}</td>
                        <td className="p-3 font-body text-xs text-ink/60">{fmtDate(c.created_at)}</td>
                        <td className="p-3"><Badge className="bg-info-blue/10 text-info-blue border-info-blue/30 font-body text-[10px] capitalize">{(c.status || '').replace('_', ' ')}</Badge></td>
                        <td className="p-3 text-right">
                          {c.certificate_url ? <a href={c.certificate_url} target="_blank" rel="noopener" className="text-brand-orange hover:underline font-body text-xs">Download</a> : <span className="font-body text-xs text-ink/30">Pending</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </TabsContent>
        </Tabs>

        <p className="font-body text-[11px] text-ink/30 mt-6 flex items-center gap-1.5">
          <CreditCard className="w-3 h-3" /> Payments processed securely by Stripe. UK VAT applied where applicable.
        </p>
      </div>
    </div>
  );
}
